import { getRoundedPrice } from '../../../../util/formatting';
import type {
  ProductService,
  DisplayService,
  ServicePricing,
} from '../../../../types/order.types';

/** Normalise every pricing field on a ServicePricing object */
export function normalisePricing(p: ServicePricing): ServicePricing {
  return {
    ...p,
    totalPrice: getRoundedPrice(p.totalPrice),
    totalAddOnPrice: getRoundedPrice(p.totalAddOnPrice),
    totalInitialPrice: getRoundedPrice(p.totalInitialPrice),
  };
}

// ---------------------------------------------------------------------------
// Slug helpers
// ---------------------------------------------------------------------------

/** Split combo slugs like `a_and_b` into an array */
export function slugToArr(slug: string | null): string[] {
  if (!slug) return [];
  return slug.includes('_and_') ? slug.split('_and_') : [slug];
}

/** Rejoin a slug array */
export function arrToSlug(arr: string[]): string {
  return arr.length > 1 ? arr.join('_and_') : arr[0] ?? '';
}

// ---------------------------------------------------------------------------
// Enrich a raw ProductService into a DisplayService
// ---------------------------------------------------------------------------

export function enrichService(
  raw: ProductService,
  type: 'primary' | 'secondary',
): DisplayService {
  let defaultPricing = raw.defaultPricing
    ? normalisePricing(raw.defaultPricing)
    : null;

  const normMap: Record<string, ServicePricing> | null = raw.map
    ? Object.fromEntries(
        Object.entries(raw.map).map(([k, v]) => [k, normalisePricing(v)]),
      )
    : null;

  // If map has exactly one entry, collapse it into defaultPricing
  let effectiveMap = normMap;
  if (normMap && Object.keys(normMap).length === 1) {
    defaultPricing = { ...Object.values(normMap)[0] };
    effectiveMap = null;
  }

  const defaultQty = raw.defaultQuantity || 1;
  const price = defaultPricing?.totalPrice ?? 0;

  return {
    ...raw,
    defaultPricing,
    map: effectiveMap,
    type,
    selectedQuantity: defaultQty,
    selectedPrice: price,
    isSelected: false,
    quantityOptions: raw.validQuantities ?? [defaultQty],
  };
}

// ---------------------------------------------------------------------------
// Price computation
// ---------------------------------------------------------------------------

/** Price for a single service given the minimum selected primary priority */
export function priceForService(
  svc: DisplayService,
  minPrimaryPriority: number | null,
): number {
  const prio = svc.primaryServicePriority ?? 999;
  const isPrimary = minPrimaryPriority === null || prio <= minPrimaryPriority;
  const qty = svc.selectedQuantity;

  if (svc.map && svc.map[qty]) {
    return isPrimary ? svc.map[qty].totalPrice : svc.map[qty].totalAddOnPrice;
  }
  if (svc.defaultPricing) {
    return isPrimary
      ? svc.defaultPricing.totalPrice
      : svc.defaultPricing.totalAddOnPrice;
  }
  return 0;
}

/** Get the initial (before-discount) price for a service at a given quantity */
export function initialPriceForService(
  svc: DisplayService,
  qty: number,
): number {
  if (svc.map && svc.map[qty]) return svc.map[qty].totalInitialPrice;
  if (svc.defaultPricing) return svc.defaultPricing.totalInitialPrice;
  return 0;
}

/** Recalculate selectedPrice on every service based on current selections */
export function recalculatePrices(
  services: DisplayService[],
): DisplayService[] {
  const selected = services.filter((s) => s.isSelected);
  const minPrio = selected.length
    ? Math.min(
        ...selected.map((s) => Number(s.primaryServicePriority ?? 999)),
      )
    : null;

  return services.map((svc) => {
    const price = priceForService(svc, minPrio);
    return { ...svc, selectedPrice: getRoundedPrice(price) };
  });
}

// ---------------------------------------------------------------------------
// Totals
// ---------------------------------------------------------------------------

export interface Totals {
  totalPrice: number;
  totalInitialPrice: number;
  totalSavingPrice: number;
}

export function computeTotals(services: DisplayService[]): Totals {
  let totalPrice = 0;
  let totalInitialPrice = 0;
  let totalSavingPrice = 0;

  for (const svc of services) {
    if (!svc.isSelected) continue;
    const price =
      typeof svc.selectedPrice === 'string'
        ? parseFloat(svc.selectedPrice)
        : svc.selectedPrice;
    totalPrice += price;

    const initial = initialPriceForService(svc, svc.selectedQuantity);
    const saving = initial - price;
    totalInitialPrice += initial;
    totalSavingPrice += saving;
  }

  return {
    totalPrice: getRoundedPrice(Math.round(totalPrice * 100) / 100),
    totalInitialPrice: getRoundedPrice(
      Math.round(totalInitialPrice * 100) / 100,
    ),
    totalSavingPrice: getRoundedPrice(
      Math.round(totalSavingPrice * 100) / 100,
    ),
  };
}
